import { useMemo } from "react";

import type { SessionUser } from "@/app/session/SessionProvider";
import { useSessionUser } from "@/app/session/useSession";
import type { Schemas } from "@/shared/api/client";

export type Permission = Schemas["Permission"];

const NO_PERMISSIONS: readonly Permission[] = [];

/** The permission keys a person holds, exactly as the server listed them. */
export function permissionsOf(user: SessionUser | undefined): ReadonlySet<Permission> {
  return new Set(user?.permissions ?? NO_PERMISSIONS);
}

/** Everything the signed-in person may do; empty when nobody is signed in. */
export function usePermissions(): ReadonlySet<Permission> {
  const user = useSessionUser();
  const list = user?.permissions ?? NO_PERMISSIONS;
  return useMemo(() => new Set(list), [list]);
}

/**
 * Whether the signed-in person may use something: a navigation entry, a button, a screen.
 * No permission means everyone may. Several keys mean any one of them. The server is the
 * source of truth: it checks every request, and this only hides what the server would refuse.
 * It reads the permission list the server sends with the person (GET /auth/me), never their
 * role's name.
 */
export function useCan(permission?: Permission | readonly Permission[]): boolean {
  const permissions = usePermissions();
  if (permission === undefined) return true;
  if (typeof permission === "string") return permissions.has(permission);
  return permission.some((key) => permissions.has(key));
}

/** Keys that open the team section: people, departments, roles or the activity log. */
export const TEAM_SECTION_PERMISSIONS = [
  "team.view",
  "team.manage",
  "departments.manage",
  "audit.view",
] as const satisfies readonly Permission[];
